import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { Users, TrendingUp, Target, Shield } from 'lucide-react';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

type LoginFormData = yup.InferType<typeof schema>;

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');
  const [isEmailNotVerified, setIsEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const justVerified = searchParams.get('verified') === 'true';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setServerError('');
    setIsEmailNotVerified(false);
    try {
      await login(data.email, data.password);
    } catch (error: any) {
      const errorCode = error.response?.data?.error?.code;
      const errorMessage = error.response?.data?.error?.message || 'Login failed';
      if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setIsEmailNotVerified(true);
        setUnverifiedEmail(data.email);
      }
      setServerError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    setServerError('');
    try {
      const { authService } = await import('../services/authService');
      await authService.resendRegistrationVerification(unverifiedEmail);
      alert('Verification email resent! Please check your inbox.');
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Side - Branding & Features */}
        <div className="hidden lg:block">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
            </h1>
            <p className="text-xl text-gray-600">
              Player &amp; Coach Development Platform for Grassroots Football
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Manage Your Squad</h3>
                <p className="text-gray-600 text-sm">Track all your players in one place with comprehensive profiles</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Track Progress</h3>
                <p className="text-gray-600 text-sm">Quarterly assessments with detailed skill tracking and insights</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Development Cycles</h3>
                <p className="text-gray-600 text-sm">Structured Assess → Identify → Focus → Reassess workflow</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Context Driven Coach Development</h3>
                <p className="text-gray-600 text-sm">Personalized coaching insights tailored to your team's specific needs</p>
              </div>
            </div>
          </div>

          {/* Club Signup Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <span className="italic font-medium">Psst</span> do you have multiple teams? You can sign up as a club by{' '}
              <Link to="/administration-club/register" className="text-green-600 hover:text-green-700 font-medium underline">clicking here</Link>
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 lg:p-10">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-3xl font-bold mb-1">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
            </h1>
            <p className="text-gray-600">Welcome back</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-green-600 hover:text-green-700 font-medium">Sign up here</Link>
            </p>
          </div>

          {/* Email verified success banner */}
          {justVerified && (
            <div className="mb-5 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-green-800">✓ Email Verified Successfully</p>
              <p className="text-sm text-green-700">Your account is now active. Sign in to get started.</p>
            </div>
          )}

          {/* Email not verified banner */}
          {isEmailNotVerified && (
            <div className="mb-5 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-yellow-800 mb-1">Email Not Verified</p>
              <p className="text-sm text-yellow-700 mb-3">Please verify your email address before logging in.</p>
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full px-4 py-2 text-sm font-medium text-yellow-800 bg-yellow-100 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? 'Resending...' : 'Resend Verification Email'}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
              <input
                type="email"
                id="email"
                placeholder="coach@example.com"
                {...register('email')}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <Link to="/forgot-password" className="text-sm text-green-600 hover:text-green-700">Forgot password?</Link>
              </div>
              <input
                type="password"
                id="password"
                placeholder="••••••••"
                {...register('password')}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.password ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-700">Remember me for 30 days</label>
            </div>

            {/* Server error */}
            {serverError && !isEmailNotVerified && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">New to GamePath?</span>
            </div>
          </div>

          {/* Pricing Info for New Users */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 border border-green-200">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 mb-1">Introductory Offer</p>
              <div className="flex items-baseline justify-center gap-2 mb-1">
                <span className="text-4xl font-bold text-gray-900">£1.99</span>
                <span className="text-gray-600">/month</span>
              </div>
              <p className="text-sm text-green-700 font-medium">For the first 3 months</p>
              <p className="text-xs text-gray-500 mt-1">Then £4.49/month • Cancel anytime</p>
            </div>
            <Link
              to="/register"
              className="block w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-center"
            >
              Sign up here
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};
